<script setup>
// Astra DS · SiteFooter — el pie.
//
// Figma: Pattern/SiteFooter-Reset 656:465.
//
// «Cuatro columnas de retícula dibujada, más línea de base. Las columnas se
// igualan en alto: si una queda corta, asoma el relleno del contenedor.»
// (Cinco desde la revisión del 24.08.26.)
//
// La retícula no se dibuja con bordes por celda sino al revés: el contenedor va
// pintado del color de la línea y los huecos de 1px entre columnas lo dejan
// asomar. Es la misma idea de siempre —la hairline ES el gutter— resuelta desde
// el otro lado, y evita el pixel doble donde dos bordes se tocan.
//
// Por eso las columnas TIENEN que igualarse en alto. Si una queda corta, por
// debajo asoma el relleno del contenedor convertido en banda.
// 🔴 Columnas que en MÓVIL se van del pie porque las recoge el panel de
// navegación (encargo de Pablo, 28.08.26). No se borran del contrato: en
// escritorio siguen ahí, y el panel no existe.
//
// Va por título y no por índice: el día que se reordene `FOOT`, un índice
// escondería la columna equivocada sin que nadie se entere.
const ocultasEnMovil = ["Social", "Escuchar"];

// Los enlaces de la app cuentan como descarga para la analítica: ver `medir`
// en las columnas.
import { medir } from "@/services/medicion";

defineProps({
  // [{ title, items: [{ label, href, to, medir }] }] — un ítem sin enlace es
  // texto. `medir` es `[evento, propiedades]` para la analítica, si el enlace
  // cuenta como una acción clave.
  // La columna «Emisión» es la frase corta de la radio: qué se emite y cómo.
  columns: { type: Array, default: () => [] },

  copyright: { type: String, default: "" },
  license: { type: String, default: "" },
});
</script>

<template>
  <footer class="site-footer">
    <div class="site-footer__cols">
      <div
        v-for="col in columns"
        :key="col.title"
        class="site-footer__col"
        :class="{ 'is-mobile-hidden': ocultasEnMovil.includes(col.title) }"
      >
        <p class="site-footer__title">{{ col.title }}</p>
        <ul class="site-footer__list">
          <li v-for="item in col.items" :key="item.label">
            <router-link
              v-if="item.to"
              class="site-footer__link"
              :to="item.to"
              >{{ item.label }}</router-link
            >
            <a
              v-else-if="item.href"
              class="site-footer__link"
              :href="item.href"
              :target="item.href.startsWith('http') ? '_blank' : null"
              :rel="item.href.startsWith('http') ? 'noopener noreferrer' : null"
              @click="item.medir && medir(...item.medir)"
              >{{ item.label }}</a
            >
            <span v-else class="site-footer__text">{{ item.label }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="site-footer__base">
      <p>{{ copyright }}</p>
      <p>{{ license }}</p>
    </div>
  </footer>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.site-footer {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  background: var(--color-bg-canvas);
  // 🔴 **La junta con la página es suya, y solo suya.** El pie vive fuera de la
  // pila de la vista —lo monta `App.vue`—, así que el hueco de la pila no llega
  // hasta aquí. Ningún módulo cierra ya por abajo, de modo que esta línea no
  // dobla con nada. Ver `scss/_estructura.scss`.
  border-top: 1px solid var(--color-border-default);
}

// El contenedor pintado del color de la línea; los huecos de 1px la dejan ver.
// `padding: 0 1px 1px` cierra la retícula por los lados y por abajo sin
// duplicar ningún trazo.
.site-footer__cols {
  display: flex;
  align-items: stretch;
  gap: 1px;
  padding: 0 1px 1px;
  background: var(--color-border-default);
}

.site-footer__col {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 16px 24px;
  background: var(--color-bg-canvas);
}

.site-footer__title {
  @include t.mono-micro;
  margin: 0 0 14px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
}

.site-footer__list {
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
}

.site-footer__link,
.site-footer__text {
  @include t.mono-link;
  display: block;
  color: var(--color-text-secondary);
  text-decoration: none;
}

.site-footer__link:hover {
  color: var(--color-text-primary);
}

.site-footer__link:focus-visible {
  outline: 2px solid var(--color-border-focus);
  outline-offset: 2px;
}

.site-footer__base {
  @include t.mono-micro;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 28px;
  color: var(--color-text-tertiary);

  p {
    margin: 0;
    white-space: nowrap;
  }
}

// ── Estrechado ──
// Cinco columnas de 16px de padding se estrangulan pronto. Envuelven en dos y
// luego en una; el `gap` de 1px sigue dibujando la retícula en las dos
// direcciones sin tocar una sola regla.
@media (max-width: 1080px) {
  .site-footer__cols {
    flex-wrap: wrap;
  }

  .site-footer__col {
    flex-basis: calc(33.333% - 1px);
  }
}

@media (max-width: 760px) {
  .site-footer__col {
    flex-basis: calc(50% - 1px);
  }

  .site-footer__base {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 14px 20px;

    p {
      white-space: normal;
    }
  }
}

// En móvil el panel ya ofrece las redes y dónde escuchar. Repetirlas aquí
// alarga un pie que en una pantalla estrecha ya es largo, y obliga a bajar
// hasta el final para algo que está a un toque en el menú.
@media (max-width: 880px) {
  .site-footer__col.is-mobile-hidden {
    display: none;
  }
}

@media (max-width: 420px) {
  .site-footer__col {
    flex-basis: 100%;
  }
}
</style>
